import React, { useState, useEffect } from 'react';
import {
  Routes,
  Route,
  Link,
  Navigate,
} from 'react-router-dom';
import {
  Alert, Button, Card, Form,
} from 'react-bootstrap';
import Chart from 'react-google-charts';
import SurveyResource from '../../common/resource/SurveyResource';
import { Icon, LoadingAnimation } from '../../common/components/Utilities';
import PeopleLayout from '../layout/PeopleLayout';
import MarkdownRenderer from '../../common/components/MarkdownRenderer';
import UserSelect from '../../common/components/DataSelect/UserSelect';
import JobSelect from '../../common/components/DataSelect/JobSelect';

function Survey() {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [type, setType] = useState(null);

  const loadQuestions = async () => {
    setLoading(true);
    const surveyData = await SurveyResource.getQuestions();
    setQuestions(surveyData.questions);
    setType(surveyData.type);
    setLoading(false);
  };

  useEffect(() => { loadQuestions(); }, []);

  return (
    <div>
      <div className="feature-header">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="my-4">
                <img src="/img/g4m.png" style={{ width: '200px' }} alt="Gear4music" />
              </div>
              <h1 style={{ fontWeight: 900 }}>Technology Team Survey</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="content container">
        <div className="row">
          <div className="col-12">
            <Routes>
              <Route path="complete" element={<SurveyQuestions loading={loading} questions={questions} type={type} />} />

              <Route path="thanks" element={<SurveyThanks />} />

              <Route path="/" element={<SurveyIntroduction loading={loading} />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}

function SurveyIntroduction({ loading }) {
  return (
    <Card className="mb-4" style={{ fontSize: '1.4em' }}>
      <Card.Body>
        <p style={{ fontWeight: 600 }}>
          This survey gives you the opportunity to share how you feel about working at
          Gear4music.
        </p>

        <p>
          We&apos;re keen to learn from everyone&apos;s opinions about
          the work we do, our culture, our communications and leadership. This will
          help make decisions about the future and steer our priorities for the months
          ahead.
        </p>

        <p>
          We ask that you take your time in completing the survey, but please do make
          sure that you respond. We&apos;d like everyone to be honest in their feedback,
          so please rest assured that we&apos;ll only report on feedback from groups of
          people rather than any one individual.
        </p>

        <div style={{ textAlign: 'right' }}>
          <Link to="/survey/complete" className="btn btn-success btn-lg mt-4">
            {loading ? 'Please wait...' : 'Get started'}
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
}

function SurveyThanks() {
  return (
    <Card className="mb-4" style={{ fontSize: '1.4em' }}>
      <Card.Body>
        <p style={{ fontWeight: 600, margin: '50px 0' }} className="text-success">
          <Icon icon="check-circle" style={{ verticalAlign: 'middle' }} size="2x" append="mr-2" />
          {' '}
          Thank you for taking the time to complete the survey!
        </p>
      </Card.Body>
    </Card>
  );
}

function SurveyQuestions({ loading, questions, type }) {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [redirect, setRedirect] = useState();

  useEffect(() => {
    const survey = document.getElementById('g4m-people-app');

    if (survey) {
      survey.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  });

  if (redirect) {
    return <Navigate to="/survey/thanks" />;
  }

  if (loading || questions === null || typeof questions === 'undefined') {
    return (
      <Card>
        <Card.Body>
          <LoadingAnimation loading={loading} inline />
        </Card.Body>
      </Card>
    );
  }

  const submitResponse = async () => {
    setSaving(true);
    try {
      await SurveyResource.submitResponse(answers);
      setRedirect(true);
    } catch (e) {
      alert(`Sorry, there was a problem saving: ${e}`);
    }
  };

  const setAnswered = (id, value) => {
    const newAnswers = { ...answers };
    newAnswers[id] = value;
    setAnswers(newAnswers);
  };

  let previousQuestionId;

  const questionElements = questions.map((question, questionKey) => {
    if (questionKey !== 0 && !answers[previousQuestionId]) {
      return null;
    }

    previousQuestionId = question.id;

    return (
      <SurveyQuestion
        key={questionKey}
        id={question.id}
        question={question}
        setAnswered={setAnswered}
        comments={type === 'full'}
      />
    );
  });

  let comments;

  if (answers[11]) {
    comments = (
      <>
        <Card className="mb-4">
          <Card.Body>
            <div style={{ fontSize: '1.3em' }}>
              Do you have any other comments you would like to add?
            </div>

            <Form.Group className="mt-4">
              <textarea maxLength={5000} className="form-control" name="comments" onChange={(event) => setAnswered('comments', event.target.value)} />
            </Form.Group>
          </Card.Body>
        </Card>

        <Button onClick={submitResponse} className="mb-4" variant="success">Submit my response</Button>
      </>
    );
  }

  return (
    <div id="survey" style={{ position: 'relative' }}>
      <LoadingAnimation loading={saving} />
      {questionElements}
      {comments}
    </div>
  );
}

function SurveyQuestion({
  id,
  question,
  setAnswered,
  comments = false,
}) {
  if (question.type === 'nps') {
    return (
      <Card className="mb-4 pb-0" id={`q-${id}`}>
        <Card.Body>
          <div style={{ fontSize: '1.3em' }}>
            {question.statement}
          </div>

          <div className="text-muted">
            {question.description}
          </div>
        </Card.Body>
        <div className="col-md-12">
          <input
            type="range"
            name={`q-${id}`}
            defaultValue={5}
            list="nps-values"
            style={{ width: '100%' }}
            min={0}
            max={10}
            onChange={(event) => setAnswered(id, event.target.value)}
          />

          <datalist
            id="nps-values"
            className="mb-4"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              writingMode: 'vertical-lr',
              width: '100%',
            }}
          >
            <option value={0} label="0 - Not at all likely" />
            <option value={1} label="1" />
            <option value={2} label="2" />
            <option value={3} label="3" />
            <option value={4} label="4" />
            <option value={5} label="5" />
            <option value={6} label="6" />
            <option value={7} label="7" />
            <option value={8} label="8" />
            <option value={9} label="9" />
            <option value={10} label="10 - Very likely" />
          </datalist>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-4 pb-0" id={`q-${id}`}>
      <Card.Body>
        <div style={{ fontSize: '1.3em' }}>
          {question.statement}
        </div>

        <div className="text-muted">
          {question.description}
        </div>
      </Card.Body>
      <Form.Group className="row" style={{ fontSize: '1.2em' }}>
        <div className="col-md-3">
          <label
            className="my-0 py-3 px-4"
            style={{ display: 'block', borderBottom: '5px solid #e35d6a' }}
            onChange={() => setAnswered(id, 1)}
          >
            <input required name={`q-${id}`} type="radio" value={1} style={{ verticalAlign: 'baseline', marginRight: '4px' }} />
            {' '}
            Strongly Disagree
          </label>
        </div>

        <div className="col-md-3">
          <label
            className="my-0 py-3 px-4"
            style={{ display: 'block', borderBottom: '5px solid #f1aeb5' }}
            onChange={() => setAnswered(id, 2)}
          >
            <input required name={`q-${id}`} type="radio" value={2} style={{ verticalAlign: 'baseline', marginRight: '4px' }} />
            {' '}
            Somewhat Disagree
          </label>
        </div>

        <div className="col-md-3">
          <label
            className="my-0 py-3 px-4"
            style={{ display: 'block', borderBottom: '5px solid #a3cfbb' }}
            onChange={() => setAnswered(id, 4)}
          >
            <input required name={`q-${id}`} type="radio" value={4} style={{ verticalAlign: 'baseline', marginRight: '4px' }} />
            {' '}
            Somewhat Agree
          </label>
        </div>

        <div className="col-md-3">
          <label
            className="my-0 py-3 px-4"
            style={{ display: 'block', borderBottom: '5px solid #479f76' }}
            onChange={() => setAnswered(id, 5)}
          >
            <input required name={`q-${id}`} type="radio" value={5} style={{ verticalAlign: 'baseline', marginRight: '4px' }} />
            {' '}
            Strongly Agree
          </label>
        </div>
        {comments === true ? (
          <div className="col-md-12">
            <label
              className="my-0 py-3 px-4"
              style={{ display: 'block' }}
            >
              Any comment?
              <input
                type="text"
                name={`q-${id}-comment`}
                style={{
                  verticalAlign: 'baseline', marginRight: '4px', marginTop: '16px', width: '100%',
                }}
                onChange={(event) => setAnswered(`${id}_comment`, event.target.value)}
              />
            </label>
          </div>
        ) : null}
      </Form.Group>
    </Card>
  );
}

export function SurveyResults(props) {
  const managerId = props.activeUser.isInGroup(1) ? null : props.activeUser.id;

  const [job, setJob] = useState(null);
  const [manager, setManager] = useState(managerId);
  const [direct, setDirect] = useState(0);
  const [total, setTotal] = useState(0);

  const header = (
    <div className="feature-header">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <h1>Technology Team Survey</h1>
          </div>
        </div>
      </div>
    </div>
  );

  let jobSelect;

  if (!managerId) {
    jobSelect = (
      <div className="col-12 col-md-3">
        <JobSelect value={job} onChange={(event) => setJob(event.target.value)} noneTitle="All Jobs" />
      </div>
    );
  }

  let directReports;

  if (manager) {
    directReports = (
      <label style={{ lineHeight: '38px', fontSize: '1.1em' }}>
        <input type="checkbox" onChange={(event) => setDirect(event.target.checked ? 1 : 0)} />
        {' '}
        Direct Reports
      </label>
    );
  }

  const content = (
    <>
      <div className="row mb-4">
        {jobSelect}

        <div className="col-12 col-md-3">
          <UserSelect value={manager} onChange={(event) => setManager(event.target.value)} titleKey="name" url="/resource/users/managers" noneTitle={managerId ? null : 'All Managers'} />
        </div>

        <div className="col-12 col-md-3">
          {directReports}
        </div>

        <div
          className="col-12 col-md-3"
          style={{
            lineHeight: '38px',
            fontSize: '1.2em',
            fontWeight: 'bold',
            textAlign: 'right',
          }}
        >
          {`${total} responses`}
        </div>
      </div>

      <SurveyCharts job={job} manager={manager} direct={direct} setTotal={setTotal} />
    </>
  );

  return (
    <PeopleLayout {...props} header={header} content={content} />
  );
}

function SurveyCharts({
  job,
  manager,
  direct,
  setTotal,
}) {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);

  const loadResults = async () => {
    setLoading(true);
    try {
      const resultData = await SurveyResource.getResults({
        job_id: job,
        manager_id: manager,
        direct,
      });
      setResults(resultData);
      setTotal(resultData.count);
    } catch (e) {
      setResults({});
      setTotal(0);
    }
    setLoading(false);
  };

  useEffect(() => { loadResults(); }, [job, manager, direct]);

  if (loading || !results.questions) {
    return (
      <Card>
        <Card.Body>
          <LoadingAnimation inline loading={loading} />
          <Alert variant="warning" hidden={loading}>
            <strong>Sorry!</strong>
            {' '}
            It looks like there isn&apos;t enough data to display yet.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const resultsOutput = results.questions.map((question, key) => (
    <SurveyResult count={results.count} data={question} key={key} />
  ));

  return (
    <>
      {resultsOutput}

      <Card>
        <Card.Header>Comments</Card.Header>
        <table className="table">
          {results.comments.map((comment, idx) => (
            <tr key={idx}>
              <td>
                <MarkdownRenderer content={comment} />
              </td>
            </tr>
          ))}
        </table>
      </Card>
    </>
  );
}

function SurveyResult({ data }) {
  if (data.type === 'nps') {
    return <SurveyResultNPS data={data} />;
  }

  const chartData = [
    ['Rating', 'Responses'],
    ['Strongly Disagree', data[1]],
    ['Somewhat Disagree', data[2]],
    ['Somewhat Agree', data[4]],
    ['Strongly Agree', data[5]],
  ];

  const colors = {
    1: '#e35d6a',
    2: '#f1aeb5',
    4: '#a3cfbb',
    5: '#479f76',
  };

  const chartOptions = {
    pieHole: 0.2,
    chartArea: {
      left: 20,
      right: 20,
      top: 20,
      bottom: 20,
    },
    slices: {
      0: { color: colors[1] },
      1: { color: colors[2] },
      2: { color: colors[4] },
      3: { color: colors[5] },
    },
  };

  const insights = [];

  const flatAverage = Math.round(data.avg);

  switch (flatAverage) {
    case 1:
      insights.push(<Insight icon="exclamation-triangle" color="danger">Responses were overwhelmingly negative.</Insight>);
      break;

    case 2:
      insights.push(<Insight icon="exclamation-circle" color="danger">Responses were overall more negative.</Insight>);
      break;

    case 3:
      insights.push(<Insight icon="map-marker-question" color="info">Responses were mixed.</Insight>);
      break;

    case 4:
      insights.push(<Insight icon="check-circle" color="success">Responses were overall more positive.</Insight>);
      break;

    case 5:
      insights.push(<Insight icon="shield-check" color="success">Responses were overwhelmingly positive.</Insight>);
      break;

    default:
      // Nothing.
  }

  if (data[1] > data[2]) {
    insights.push(<Insight icon="exclamation-triangle" color="danger">Those who disagreed with this tended to feel strongly about it.</Insight>);
  }

  if (data[5] > data[4]) {
    insights.push(<Insight icon="shield-check" color="success">Those who agreed with this tended to feel strongly about it.</Insight>);
  }

  return (
    <Card className="mb-4">
      <Card.Header>{data.statement}</Card.Header>
      <Card.Body>
        <div className="row">
          <div className="col-lg-6">
            <Chart
              width="100%"
              height="400px"
              chartType="PieChart"
              data={chartData}
              options={chartOptions}
            />
          </div>
          <div className="col-lg-6">
            {insights}
          </div>
        </div>
      </Card.Body>
      <table className="table my-0">
        <tbody>
          {data.comments.map((comment) => (
            <tr>
              <td style={{ borderLeftWidth: '10px', borderLeftStyle: 'solid', borderLeftColor: colors[comment.score] }}>{comment.comment}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function SurveyResultNPS({ data }) {
  console.log(data);

  const count = data[0]
    + data[1]
    + data[2]
    + data[3]
    + data[4]
    + data[5]
    + data[6]
    + data[7]
    + data[8]
    + data[9]
    + data[10];
  const detractorTotal = data[0] + data[1] + data[2] + data[3] + data[4] + data[5] + data[6];
  const promoterTotal = data[9] + data[10];

  const detractorPercent = Math.round((detractorTotal / count) * 100);
  const promoterPercent = Math.round((promoterTotal / count) * 100);

  const score = Math.round(promoterPercent - detractorPercent);

  let scoreClass = 'text-warning';

  if (score >= 10) {
    scoreClass = 'text-success';
  }

  if (score <= -10) {
    scoreClass = 'text-danger';
  }

  return (
    <Card className="mb-4">
      <Card.Header>{data.statement}</Card.Header>
      <Card.Body>
        <div className="row">
          <div className="col-md-8">
            <table className="table my-0">
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>0</th>
                  <th style={{ textAlign: 'center' }}>1</th>
                  <th style={{ textAlign: 'center' }}>2</th>
                  <th style={{ textAlign: 'center' }}>3</th>
                  <th style={{ textAlign: 'center' }}>4</th>
                  <th style={{ textAlign: 'center' }}>5</th>
                  <th style={{ textAlign: 'center' }}>6</th>
                  <th style={{ textAlign: 'center' }}>7</th>
                  <th style={{ textAlign: 'center' }}>8</th>
                  <th style={{ textAlign: 'center' }}>9</th>
                  <th style={{ textAlign: 'center' }}>10</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ textAlign: 'center' }} className="bg-danger text-white">{data[0]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-danger text-white">{data[1]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-danger text-white">{data[2]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-danger text-white">{data[3]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-danger text-white">{data[4]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-danger text-white">{data[5]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-danger text-white">{data[6]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-warning text-white">{data[7]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-warning text-white">{data[8]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-success text-white">{data[9]}</td>
                  <td style={{ textAlign: 'center' }} className="bg-success text-white">{data[10]}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-md-4">
            <h4 style={{ textAlign: 'center' }}>Net Promoter Score</h4>
            <div style={{ textAlign: 'center', fontSize: '40px', fontWeight: 'bold' }} className={scoreClass}>
              {score}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

function Insight({ children, color, icon }) {
  return (
    <div className="p-2">
      <Icon variant="fad" size="lg" append={`text-${color} mr-2`} icon={icon} />
      {' '}
      {children}
    </div>
  );
}

export default Survey;
